<template>
  <a-modal
    v-model:visible="data.show"
    centered
    :width="430"
    @cancel="close"
    :footer="null"
    :maskClosable="false"
    :title="null"
    :closable="false"
  >
    <div class="call-modal">
      <div class="title">
        <ExclamationCircleFilled class="icon" />
        确定拨打患者电话
      </div>
      <div class="section-title">近一周主动回访记录</div>
      <div class="record-table">
        <div v-if="data.records.length" class="table-body">
          <div
            v-for="(item, index) in data.records"
            :key="index"
            class="table-row"
            :class="{ 'today-row': isToday(item.callTime) }"
          >
            <span class="table_item">
              {{ formatDate(item.callTime) }}
            </span>
            <span class="table_item">回访健管：{{ item.doctorAssistantName }}</span>
            <span class="table_item">{{ item.callStatusDesc }}</span>
          </div>
        </div>
        <div v-else class="empty">暂无记录</div>
      </div>
      <div class="footer">
        <div class="left">
          <slot name="footerBtn" :record="data.form" />
        </div>
        <div class="right">
          <a-button class="btn" @click="close">取消</a-button>
          <a-button class="btn" type="primary" @click="confirm">拨打</a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { updateMatters } from '@/api/HealthRecord'
import { callOut } from '@/utils/Tccc'
import { ExclamationCircleFilled } from '@ant-design/icons-vue'
import { defineEmits, defineExpose, reactive } from 'vue'
import moment from 'moment'
import { message, Modal } from 'ant-design-vue'
import { checkPatientWorkOrder } from '@/api/UserPool/activeRevisit.js'

const emit = defineEmits(['confirm', 'close'])
const data = reactive({
  show: false,
  records: [],
  hasWorkOrder: false,
  form: {}
})

const formatDate = dateTime => {
  if (!dateTime) return ''
  const m = moment(dateTime)
  if (!m.isValid()) return dateTime.toString().trim()

  if (m.isSame(moment(), 'day')) {
    return `今天 ${m.format('HH:mm')}`
  }
  if (m.isSame(moment().add(1, 'day'), 'day')) {
    return `明天 ${m.format('HH:mm')}`
  }
  if (m.isSame(moment().subtract(1, 'day'), 'day')) {
    return `昨天 ${m.format('HH:mm')}`
  }
  if (m.isSame(moment().subtract(2, 'day'), 'day')) {
    return `前天 ${m.format('HH:mm')}`
  }

  return m.format('YYYY-MM-DD HH:mm')
}

const isToday = dateTime => {
  if (!dateTime) return false
  const m = moment(dateTime)
  return m.isValid() && m.isSame(moment(), 'day')
}

const close = () => {
  data.show = false
  emit('close')
}

const confirm = () => {
  emit('confirm')
  data.show = false
  // 拨打电话逻辑
  call()
}
// 加载模拟数据
const loadMockData = async () => {
  try {
    // 调用检查患者工单接口
    const result = await checkPatientWorkOrder({ ucPatientId: data.form.ucPatientId })

    // 如果有工单，显示提示
    if (result.hasWorkOrder) {
      // message.warning('监控到患者已有工单生成，无法进行主动回访。')
      Modal.warning({
        title: () => '提醒',
        content: () => '监控到患者已有工单生成，无法进行主动回访。'
      })
      data.records = []
    } else {
      // 保存接口返回的数据
      data.show = true
      data.records = result.callRecords || []
    }
  } catch (error) {
    console.error('检查患者工单失败:', error)
    message.error('检查患者工单失败，请稍后重试')
  }
}
// 初始化
// activeFollowupFlag 是否是主动回访 0-否 1-是
// crossPoolParams 跨回访池确认拨打埋点参数（sourcePage/crossPoolConfirmed/crossPoolTargetFollowupId），随拨打参数透传
const init = (params, activeFollowupFlag = 0, crossPoolParams = {}) => {
  // 加载工单检查数据
  data.form = params
  data.activeFollowupFlag = activeFollowupFlag
  data.crossPoolParams = crossPoolParams
  loadMockData()
}
// 拨打电话
const call = () => {
  const params = {
    calleeType: 1,
    workOrderIds: [],
    businessType: 0,
    doctorId: data.form.doctorId,
    calleeUcUserId: data.form.ucPatientId,
    activeFollowupFlag: data.activeFollowupFlag,
    ...data.crossPoolParams
  }
  callOut(params, {
    userType: '患者',
    name: data.form.patientName
  }).then(result => {
    if (result.cdrId) {
      const params = {
        cdrId: result.cdrId,
        serviceMatters: '',
        mattersRemark: ''
      }
      updateMatters(params)
    }
  })
}

defineExpose({ init })
</script>

<style scoped lang="scss">
.call-modal {
  .title {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    display: flex;
    align-items: center;
    .icon {
      color: #faad14;
      font-size: 22px;
      margin-right: 12px;
    }
  }
  .section-title {
    color: #999;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin: 12px 0 8px;
  }
  .record-table {
    border-radius: 4px;
    background: #f7f8fb;
    padding: 16px;
    max-height: 130px;
    overflow: auto;
    font-size: 14px;
    .table-body {
      .table-row {
        margin-bottom: 8px;
        display: flex;
        justify-content: space-between;
        &:last-child {
          margin-bottom: 0;
        }

        .table_item {
          display: inline-block;
          width: calc((100% - 50px) / 2);
          &:last-child {
            text-align: right;
            width: 50px;
            flex-shrink: 0;
          }
        }
      }
      .today-row {
        color: #f5222d;
      }
    }
  }
  .footer {
    margin-top: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .btn {
      margin-left: 8px;
      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
